import { useEffect, useState } from "react";
import { SettingsField } from "@/components/v2/settings-field";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useCreateBusiness,
  useUpdateBusiness,
} from "@/hooks/use-account-businesses";
import type { Business } from "@adva/types";

export function BusinessFormSheet({
  open,
  onClose,
  business,
}: {
  open: boolean;
  onClose: () => void;
  business?: Business | null;
}) {
  const createBusiness = useCreateBusiness();
  const updateBusiness = useUpdateBusiness();
  const isEdit = !!business;

  const [name, setName] = useState("");
  const [vertical, setVertical] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(business?.name ?? "");
      setVertical(business?.vertical ?? "");
      setDescription(business?.description ?? "");
      setWebsite(business?.website ?? "");
      setError(null);
    }
  }, [open, business]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit && business) {
        await updateBusiness.mutateAsync({
          id: business.id,
          name: name.trim(),
          vertical: vertical.trim() || null,
          description: description.trim() || null,
          website: website.trim() || null,
        });
      } else {
        await createBusiness.mutateAsync({
          name: name.trim(),
          vertical: vertical.trim() || undefined,
          description: description.trim() || undefined,
          website: website.trim() || undefined,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {isEdit ? "Edit business" : "Create business"}
          </SheetTitle>
        </SheetHeader>
        <form
          id="business-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto space-y-4 py-4"
        >
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <SettingsField label="Name" required>
            <Input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Canopy Turf"
            />
          </SettingsField>
          <SettingsField label="Vertical">
            <Input
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              placeholder="e.g. lawn-care, coatings, painting"
            />
          </SettingsField>
          <SettingsField label="Description">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </SettingsField>
          <SettingsField label="Website">
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
            />
          </SettingsField>
        </form>
        <FormSheetFooter
          onCancel={onClose}
          submitLabel={isEdit ? "Save changes" : "Create business"}
          isSubmitting={submitting || !name.trim()}
          formId="business-form"
        />
      </SheetContent>
    </Sheet>
  );
}
